import React, { useEffect, useState, useRef } from "react";
import Preloader from "../Components/Preloader";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import AOS from "aos";
import ReCAPTCHA from "react-google-recaptcha";
import confetti from "canvas-confetti";

function Winner() {
  const [isLoading, setIsLoading] = useState(true);
  const [referenceNo, setReferenceNo] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [resultData, setResultData] = useState(null); // Stores the full API response data
  const [apiLoading, setApiLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const modalRef = useRef(null);
  const referenceHelpModal = useRef(null);
  const tcsModal = useRef(null);

  // Preloader
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 500);
  }, []);

  // AOS init
  useEffect(() => {
    AOS.init({
      duration: 750,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  // SIDE CONFETTI (Gentle background effect)
  useEffect(() => {
    const interval = setInterval(() => {
      // Only fire loose confetti if we are not in a focused interaction
      const randomX = Math.random();
      if (randomX < 0.2 || randomX > 0.8) {
        confetti({
          particleCount: 20,
          spread: 60,
          origin: { x: randomX, y: 0.2 },
          colors: ['#eba12a', '#ffd38b', '#ffffff'],
          disableForReducedMotion: true,
          scalar: 0.8
        });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  // CAPTCHA
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (token) setErrorMessage(""); // Clear error on captcha success
  };

  // Helper: Get Prize Image Based on Result
  const getPrizeImage = () => {
    if (!resultData) return "";

    // Check specific result codes from requirement
    const resValue = resultData.result;

    if (resValue === "1") {
      // Winner: TV
      return "/assets/images/offer/led_img.png";
    }
    if (resValue === "2") {
      // Winner: Alexa
      return "/assets/images/offer/alex_img.png";
    }
    // "0" or any other fallback
    return "/assets/images/offer/sad-next-time.png";
  };

  // SEARCH API
  const handleSearch = async () => {
    setErrorMessage("");
    setResultData(null); // Reset previous result

    if (!referenceNo.trim()) {
      setErrorMessage("Please enter your reference number.");
      return;
    }

    if (!captchaToken) {
      setErrorMessage("Please complete the CAPTCHA.");
      return;
    }

    try {
      setApiLoading(true);

      const payload = {
        ReferenceNo: referenceNo.trim(),
        reCaptcha: captchaToken
      };

      const response = await fetch("https://ifepay.co.uk/prod/api/contactus/result", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("API Response:", data);

      // Handle Logic based on requirements:
      // Case 1: Wrong referenceNo -> { response: false, responseCode: "02", data: "Result not found." }
      // Case 2: Not winner -> { response: false, responseCode: "02", data: "Better luck next time", result: "0" }
      // Case 3: Winner -> { response: true, responseCode: "00", data: "result found", result: "1" (or "2") }

      if (data.responseCode === "00" && data.response === true) {
        // WINNER
        setResultData(data);
        // triggerWinnerConfetti(); // Moved to useEffect for reliability
        showModal();
      } else if (data.responseCode === "02") {
        if (data.result === "0") {
          // PARTICIPANT BUT NOT WINNER
          setResultData(data);
          showModal();
        } else {
          // WRONG REFERENCE / NOT FOUND
          setErrorMessage(data.data || "Result not found.");
        }
      } else {
        // Fallback for other codes
        setErrorMessage(data.data || "Something went wrong. Please check your input.");
      }

    } catch (error) {
      console.error("Search Error:", error);
      setErrorMessage("Unable to connect to the server. Please try again later.");
    } finally {
      setApiLoading(false);
      // Optional: Reset captcha if needed, but usually better to let user keep token for a bit or rely on expiration
      // window.grecaptcha.reset(); 
    }
  };

  const showModal = () => {
    if (modalRef.current) {
      const modal = new window.bootstrap.Modal(modalRef.current);
      modal.show();
    }
  };

  // const handleCaptchaSubmit = (token) => {
  //   setCaptchaToken(token);
  //   console.log("Captcha submitted token", token);
  // };

  // Trigger Confetti when Winner is determined
  useEffect(() => {
    if (resultData && (resultData.result === "1" || resultData.result === "2")) {
      triggerWinnerConfetti();
    }
  }, [resultData]);

  const triggerWinnerConfetti = () => {
    // Big burst for winners
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 100, zIndex: 9999, disableForReducedMotion: true };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      // Confetti falling from top-centerish areas
      confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.4), y: Math.random() - 0.2 } }));
      confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.6, 0.9), y: Math.random() - 0.2 } }));
    }, 250);
  };

  return (
    <>
      {isLoading ? (
        <Preloader />
      ) : (
        <>
          <Header />

          {/* Banner */}
          <div className="banner-area bg-color-theme position-relative overflow-hidden">
            <div className="container">
              <div className="banner-content" data-aos="zoom-in-up" data-aos-delay={500}>
                <h1 className="text-white">
                  Happy Holidays Raffle Winners Announcement!
                </h1>
                <p className="mb-1" style={{ color: "#ffd38b" }}>
                  Ifepay congratulates all the lucky winners and is thankful to all
                  the entrants.
                </p>
              </div>

              {/* Search Area */}
              <div className="text-center mt-5" data-aos="fade-up" data-aos-delay={400}>
                <label className="text-white mb-2">
                  Your reference no. e.g IF00000001
                </label>

                <div
                  className="d-flex align-items-center p-2 rounded mx-auto"
                  style={{
                    maxWidth: "500px",
                    background: "transparent",
                    border: "1px solid #fff",
                  }}
                >
                  <input
                    type="text"
                    className="form-control border-0 text-white"
                    style={{ background: "transparent", color: "white" }}
                    placeholder="Enter reference number"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearch();
                    }}
                  />

                  <button
                    className="btn btn-success rounded-circle ms-2"
                    onClick={handleSearch}
                    disabled={apiLoading}
                  >
                    {apiLoading ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <i className="fa fa-search"></i>
                    )}
                  </button>
                </div>

                {errorMessage && (
                  <div className="mt-3 animate__animated animate__headShake">
                    <span className="badge bg-danger p-2 fs-6">{errorMessage}</span>
                  </div>
                )}

                {/* HELP LINK */}
                <p
                  className="text-white mt-3 fw-bold text-decoration-underline"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    new window.bootstrap.Modal(referenceHelpModal.current).show()
                  }
                >
                  Where can I find my reference number?
                </p>
                {/* dev test */}
                {/* <div className="mb-3">
                  <label htmlFor="captcha" className="form-label text-white">Enter Captcha</label>
                  <input type="text" className="form-control" id="captcha" placeholder="Enter Captcha" />
                  <button className="btn btn-success" onClick={() => {
                    const captcha = document.getElementById("captcha").value;
                    if (captcha) {
                      handleCaptchaSubmit(captcha);
                    }
                  }} >Submit</button>
                </div> */}

                {/* Ts & Cs */}
                <p
                  className="text-white text-decoration-underline"
                  style={{ cursor: "pointer" }}
                  onClick={() => new window.bootstrap.Modal(tcsModal.current).show()}
                >
                  Ts & Cs Apply*
                </p>

                {/* CAPTCHA */}
                <div className="mt-3 d-flex justify-content-center">
                  <ReCAPTCHA
                    sitekey="6LdJXrcqAAAAAICxBuvcI8Ndx7sROqAy_tHJ2JQH"
                    onChange={handleCaptchaChange}
                    theme="dark"
                  />
                </div>
              </div>
            </div>

            {/* Shapes */}
            <div className="shape-image">
              <img
                className="hero-shape-1 moveHorizontal_reverse"
                src="assets/images/offer/gift-box.png"
                alt="image"
                data-aos="zoom-in-up"
                data-aos-delay={500}
              />

              <img
                className="hero-shape-2 moveHorizontal_reverse"
                src="assets/images/offer/gift-box.png"
                alt="image"
                data-aos="zoom-in-up"
                data-aos-delay={500}
              />
            </div>
          </div>

          {/* RESULT MODAL */}
          <div className="modal fade" id="resultModal" ref={modalRef} tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content custom-modal-content border-0 overflow-hidden">
                {/* Decorative Header Background */}
                <div className={`modal-header-accent ${resultData?.result === "1" || resultData?.result === "2" ? "bg-winner" : "bg-participant"}`}>
                  <button type="button" className="btn-close-custom" data-bs-dismiss="modal" aria-label="Close">
                    <i className="fa fa-times"></i>
                  </button>
                </div>

                <div className="modal-body text-center pt-0 pb-4 px-4">
                  {resultData ? (
                    <div className="content-wrapper">
                      {/* Image with Float Animation */}
                      <div className="image-container mb-4">
                        <img
                          src={getPrizeImage()}
                          alt="Result"
                          className="img-fluid floating-anim"
                          style={{ maxHeight: "220px", objectFit: 'contain' }}
                        />
                      </div>

                      {(resultData.result === "1" || resultData.result === "2") ? (
                        <div className="animate-fade-up">
                          <h2 className="mb-2 gradient-text fw-bold animate__animated animate__tada">
                            🎉 CONGRATULATIONS!
                          </h2>
                          <p className="fs-5 text-muted mb-4">
                            You have won a brand new <br />
                            <strong className="text-dark fs-4">{resultData.result === "1" ? "75-Inch TV" : "Amazon Alexa"}</strong>!
                          </p>
                        </div>
                      ) : (
                        <div className="animate-fade-up">
                          <h3 className="mb-3 fw-bold" style={{ color: "#2c3e50" }}>
                            So close! 🎁
                          </h3>
                          <p className="text-secondary mb-4 px-3" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                            You didn’t win this time, but don’t worry—better luck next time. <br />
                            <span className="fw-bold" style={{ color: "#eba12a" }}>IFEPAY</span> has many more surprises lined up!
                          </p>
                        </div>
                      )}

                      {/* Action Button */}
                      <button className="btn btn-premium w-100 rounded-pill py-3 fw-bold shadow-sm" data-bs-dismiss="modal">
                        {resultData?.result === "1" || resultData?.result === "2" ? "Claim Prize" : "Okay, Got it!"}
                      </button>
                    </div>
                  ) : (
                    <p className="py-5">No result to display.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* HELP MODAL */}
          <div className="modal fade" id="referenceHelp" ref={referenceHelpModal}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Where Can I Find My Reference Number?</h5>
                  <button className="btn-close" data-bs-dismiss="modal"></button>
                </div>

                <div className="modal-body text-center">
                  <img
                    src="/assets/images/offer/reference-number.png"
                    alt="reference"
                    className="img-fluid rounded"
                  />
                  <p className="mt-3">
                    Your reference number appears in the Settings section of the Ifepay App.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Ts & Cs MODAL */}
          <div className="modal fade" id="tcsModal" ref={tcsModal}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Ts & Cs Apply*</h5>
                  <button className="btn-close" data-bs-dismiss="modal"></button>
                </div>

                <div className="modal-body">
                  <p>
                    • Winners must comply with all Ifepay rules and eligibility criteria.
                    <br />
                    • Reference numbers must be valid and unique.
                    <br />
                    • Prizes are non-transferable.
                    <br />
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Footer />

          {/* Styles */}
          <style>{`
            /* --- CUSTOM MODAL STYLES --- */
            .custom-modal-content {
              border-radius: 24px;
              background: #fff;
              box-shadow: 0 10px 40px rgba(0,0,0,0.2);
              position: relative;
            }
            .modal-header-accent {
              height: 100px;
              width: 100%;
              border-bottom-left-radius: 50% 20px;
              border-bottom-right-radius: 50% 20px;
              position: relative;
              margin-bottom: -50px; /* Pull content up */
              z-index: 0;
            }
            .bg-winner {
              background: linear-gradient(135deg, #198754, #20c997);
            }
            .bg-participant {
              background: linear-gradient(135deg, #FF9966, #FF5E62); /* Warm gradient */
            }
            .btn-close-custom {
              position: absolute;
              top: 15px;
              right: 15px;
              background: rgba(255,255,255,0.2);
              border: none;
              border-radius: 50%;
              width: 32px;
              height: 32px;
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.2s;
              z-index: 2;
            }
            .btn-close-custom:hover {
              background: rgba(255,255,255,0.4);
              transform: rotate(90deg);
            }
            .content-wrapper {
              position: relative;
              z-index: 1;
            }
            .floating-anim {
              animation: float 3s ease-in-out infinite;
              filter: drop-shadow(0 10px 15px rgba(0,0,0,0.15));
            }
            @keyframes float {
              0% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
              100% { transform: translateY(0px); }
            }
            .animate-fade-up {
              animation: fadeUp 0.6s ease-out forwards;
              opacity: 0;
              transform: translateY(20px);
              animation-delay: 0.2s;
            }
            @keyframes fadeUp {
              to { opacity: 1; transform: translateY(0); }
            }
            .gradient-text {
              background: linear-gradient(45deg, #198754, #20c997);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            .btn-premium {
              background: linear-gradient(90deg, #eba12a, #f3c259);
              border: none;
              color: #fff;
              font-size: 1.1rem;
              transition: transform 0.2s, box-shadow 0.2s;
            }
            .btn-premium:hover {
              transform: translateY(-2px);
              box-shadow: 0 5px 15px rgba(235, 161, 42, 0.4) !important;
              color: #fff;
            }

            /* Placeholders specific */
            .form-control::placeholder {
              color: rgba(255, 255, 255, 0.7) !important;
            }
            .form-control:focus {
                box-shadow: none;
                border-color: #ffd38b;
            }
          `}</style>
        </>
      )}
    </>
  );
}

export default Winner;
